<template>
  <div class="flex flex-col gap-4 min-h-[200px]">
    <div class="text-sm text-text-subtle">
      {{ is_edit ? $t('scheduled.edit.editIntro') : $t('scheduled.edit.createIntro') }}
    </div>

    <!-- An event trigger's type is shown, not offered, for the watcher's reason below: what it reacts to
         was compiled from the person's words at creation, and this form cannot author another. -->
    <div v-if="is_event_driven" class="flex items-center gap-2">
      <span class="text-xs font-medium text-text-default">Type</span>
      <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link">Event</span>
      <span class="text-xs text-text-subtle">Rey reacts each time it happens. To change what it reacts to, create a new event trigger.</span>
    </div>

    <!-- Task type. A watcher's type is shown, not offered: changing it would need the compiled
         condition this form cannot author. -->
    <div v-if="is_watcher" class="flex items-center gap-2">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.typeLabel') }}</span>
      <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link">{{ $t('scheduled.common.taskTypeWatcher') }}</span>
      <span class="text-xs text-text-subtle">{{ $t('scheduled.edit.watcherChangeHint') }}</span>
    </div>

    <!-- The watched line, correctable. The SUBJECT (the tool and its arguments) stays read-only and the
         server preserves it whatever this form sends: the fetch proven at creation stays the fetch, and
         changing what is watched is a different watcher. What a person can fix here is what the extraction
         may have got wrong — the number, the direction, and which field. -->
    <div v-if="is_watcher && is_edit" class="flex flex-col gap-2 p-3 bg-surface-1 border border-border-separator rounded-md">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.watchesLabel') }}</span>
      <span class="text-xs text-text-subtle font-mono truncate" :title="form.condition && form.condition.tool">
        {{ (form.condition && form.condition.tool) || $t('scheduled.edit.noCompiledCondition') }}
      </span>

      <div class="flex gap-3">
        <label class="flex flex-col gap-1 flex-1">
          <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.fieldLabel') }}</span>
          <!-- A select of provably valid fields rather than a text box: the server refuses anything the
               tool cannot report, so offering free text would offer choices the save rejects. When nothing
               is known about the tool the list holds only what the watcher already has. -->
          <fusion-dropdown
            v-model="form.condition.filterField"
            name="conditionField"
            :options="condition_field_options"
            sort-order="none"
            compact
            @change="markChanged"
          />
        </label>
        <label class="flex flex-col gap-1 flex-1">
          <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.whenLabel') }}</span>
          <fusion-dropdown
            v-model="form.condition.comparator"
            name="conditionComparator"
            :options="condition_comparator_options"
            sort-order="none"
            compact
            @change="markChanged"
          />
        </label>
        <label class="flex flex-col gap-1 w-28">
          <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.thanLabel') }}</span>
          <input
            v-model.number="form.condition.threshold"
            type="number"
            step="any"
            name="conditionThreshold"
            class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
            @input="markChanged"
          />
        </label>
      </div>
      <span class="text-xs text-text-subtle">
        {{ $t('scheduled.edit.thresholdHint') }}
      </span>
    </div>
    <!-- A recap's type is shown, not offered, like the two above. Watcher needs a compiled condition this
         form cannot author, and MorningBrief is one per person, toggled from MorningBriefOptIn — so there
         is nothing left to change it to. -->
    <div v-else-if="!is_event_driven && !is_watcher" class="flex items-center gap-2" data-test="recap-type">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.typeLabel') }}</span>
      <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-subtle text-text-link">Recap</span>
      <span class="text-xs text-text-subtle">Answers a question on a schedule.</span>
    </div>

    <!-- Name -->
    <label class="flex flex-col gap-1">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.nameLabel') }}</span>
      <input
        v-model="form.name"
        type="text"
        maxlength="120"
        :placeholder="$t('scheduled.edit.namePlaceholder')"
        class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        @input="markChanged"
      />
    </label>

    <!-- Prompt. Not shown for a watcher, whose work is its compiled condition. -->
    <label v-if="prompt_required" class="flex flex-col gap-1">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.promptLabel') }}{{ is_event_driven ? ' (optional)' : '' }}</span>
      <textarea
        v-model="form.prompt"
        rows="3"
        :placeholder="$t('scheduled.edit.promptPlaceholder')"
        class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default resize-y outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        @input="markChanged"
      />
    </label>

    <!-- Cadence + time -->
    <!-- None of the schedule controls render for an event trigger: it has no clock, so each would be a
         control with no effect — and a person changing "How often" would reasonably believe it did something. -->
    <div v-if="!is_event_driven" class="flex gap-3">
      <label class="flex flex-col gap-1 flex-1">
        <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.howOftenLabel') }}</span>
        <fusion-dropdown
          v-model="form.trigger.cadence"
          name="cadence"
          :options="cadence_options"
          sort-order="none"
          compact
          @change="markChanged"
        />
      </label>
      <label class="flex flex-col gap-1 w-32">
        <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.timeLabel') }}</span>
        <!-- Disabled rather than hidden while anchored, and the stored value is left untouched: an
             anchored task's fire time comes from the store's hours, so an editable clock here would be a
             control with no effect. Keeping it visible means switching back to "A set time" restores the
             time the task already had instead of silently inventing one. -->
        <!-- A dropdown rather than a native time input, whose clock popup detached from the iframe when
             the host page scrolled. FusionDropdown dims itself while disabled. -->
        <!-- Disabled on an Interval task too (PR #1129 review): it runs every N minutes, so a clock time has no
             effect, as the task row and the schedule summary already say by not showing one. -->
        <fusion-dropdown
          v-model="form.trigger.time"
          name="time"
          :options="time_dropdown_options"
          :disabled="(anchor_available && is_anchored) || form.trigger.cadence === 'Interval'"
          sort-order="none"
          compact
          @change="markChanged"
        />
      </label>
    </div>

    <!-- Store-hours anchor (NPM-29). The whole block is absent unless the deployment enables it — see
         anchor_available for why that is not the per-rooftop beta flag. -->
    <div v-if="anchor_offered && !is_event_driven" class="flex flex-col gap-1">
      <div class="flex gap-3">
        <label class="flex flex-col gap-1 flex-1">
          <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.runsAtLabel') }}</span>
          <fusion-dropdown
            v-model="form.trigger.storeHoursAnchor"
            name="storeHoursAnchor"
            :options="anchor_options"
            sort-order="none"
            compact
            @change="markChanged"
          />
        </label>
        <label v-if="is_anchored" class="flex flex-col gap-1 w-32">
          <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.offsetLabel') }}</span>
          <input
            v-model.number="form.trigger.anchorOffsetMinutes"
            type="number"
            step="5"
            name="anchorOffsetMinutes"
            class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
            @input="markChanged"
          />
        </label>
      </div>
      <span v-if="is_anchored" class="text-xs text-text-subtle">
        {{ $t('scheduled.edit.anchorHint', { anchor: anchor_summary }) }}
      </span>
    </div>

    <!-- Weekly day -->
    <label v-if="form.trigger.cadence === 'Weekly' && !is_event_driven" class="flex flex-col gap-1">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.dayOfWeekLabel') }}</span>
      <fusion-dropdown
        v-model="form.trigger.dayOfWeek"
        name="dayOfWeek"
        :options="weekday_options"
        sort-order="none"
        compact
        @change="markChanged"
      />
    </label>

    <!-- Monthly day -->
    <label v-if="form.trigger.cadence === 'Monthly' && !is_event_driven" class="flex flex-col gap-1">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.dayOfMonthLabel') }}</span>
      <input
        v-model.number="form.trigger.dayOfMonth"
        type="number"
        min="1"
        max="31"
        class="text-sm px-2 py-1.5 bg-surface-1 border border-border-separator rounded-md text-text-default w-24 outline-none transition-[background-color,border-color,box-shadow] focus:bg-field-focus focus:border-[#0855F9] focus:shadow-[0_0_0_3px_var(--shadow-focus-input)]"
        @input="markChanged"
      />
    </label>

    <!-- Store. Read-only: an edit cannot move a task between rooftops — the PUT preserves identity
         fields — and the store is what tells two identically named tasks apart, so it has to be
         visible even though it cannot be changed here. -->
    <div class="flex items-center gap-2">
      <span class="text-xs font-medium text-text-default">Store</span>
      <span class="text-xs text-text-subtle truncate">{{ store_label }}</span>
    </div>

    <!-- Timezone. A picker rather than free text: the value is resolved server-side by
         FindSystemTimeZoneById, which throws on an unknown id and is called unguarded, so a typo here
         surfaced as a 500. An existing value outside the list stays listed so opening the form never
         rewrites a task's schedule.

         Stated at rest and edited on request. The design doc's Considerations section asks for the zone
         a task inherits to be shown back to the user — two defects have already come from implicit zone
         resolution — but a picker sitting open invites a change nobody came to make, on the one field
         where a wrong value moves every future run without failing. -->
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.timeZoneLabel') }}</span>
        <span class="text-xs text-text-subtle flex-1 truncate">{{ selected_timezone_label }}</span>
        <button
          type="button"
          class="text-xs text-text-link flex-shrink-0"
          @click="editing_timezone = !editing_timezone"
        >{{ editing_timezone ? 'Done' : 'Change' }}</button>
      </div>
      <fusion-dropdown
        v-if="editing_timezone"
        v-model="form.trigger.timeZone"
        name="timeZone"
        :options="timezone_dropdown_options"
        sort-order="none"
        compact
        @change="markChanged"
      />
    </div>

    <!-- Delivery -->
    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.edit.deliveryLabel') }}</span>
      <!-- Clickable since email has a transport. It was disabled while in-app was the only channel
           that could deliver anything; switching it off then would have meant a task that runs and
           tells nobody. setChannel still refuses an EMPTY set, which is the guard that actually
           matters — an email-only task is a choice, silence is not. -->
      <label class="flex items-center gap-2 text-sm text-text-default">
        <input
          type="checkbox"
          name="inAppChannel"
          :checked="inapp_enabled"
          @change="setChannel('InApp', $event.target.checked)"
        />
        <span>{{ $t('scheduled.edit.inAppChannel') }}</span>
      </label>
      <label class="flex items-center gap-2 text-sm text-text-default">
        <input
          type="checkbox"
          name="emailChannel"
          :checked="email_enabled"
          @change="setChannel('Email', $event.target.checked)"
        />
        <span>{{ $t('scheduled.edit.emailChannel') }}</span>
      </label>
      <!-- Hidden rather than disabled when no notification hub is configured. A toggle that cannot
           deliver anything is exactly the defect the availability endpoint was created to remove, and
           push is a deployment-wide capability rather than a per-rooftop one. -->
      <label v-if="push_available" class="flex items-center gap-2 text-sm text-text-default">
        <input
          type="checkbox"
          name="pushChannel"
          :checked="push_enabled"
          @change="setChannel('Push', $event.target.checked)"
        />
        <span>{{ $t('scheduled.edit.pushChannel') }}</span>
      </label>
      <span class="text-xs text-text-subtle">
        {{ $t('scheduled.edit.emailNote') }}
      </span>
      <span v-if="push_available" class="text-xs text-text-subtle">
        {{ $t('scheduled.edit.pushNote') }}
      </span>
    </div>

    <div v-if="error_message" class="text-xs text-danger px-1">{{ error_message }}</div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import FusionDropdown from '@components/UI/FusionDropdown.vue';
import { timezoneOptionsFor, timezoneDropdownOptions, browserTimeZone } from '../../utils/timezones';
import { anchorName, describeAnchor } from '../../utils/storeHoursAnchor';
import { weekdayDropdownOptions, weekdayOrdinal } from '../../utils/weekdays';
import { timeOfDayOptions, padTime } from '../../utils/timeOfDay';

// ConditionComparator, mirroring the C# enum's NAMES (the API sends and accepts names, not ordinals) and
// the words WatcherConditionCompiler.Describe renders. Only a fallback: the server's per-watcher list is
// narrower, because a template restricts which comparisons it allows, and the save enforces that list.
// Labels here are i18n KEYS (scheduled.common.comparator*); rendering goes through comparatorLabel() so
// a server-supplied label (already human words) passes through untranslated while the fallback localizes.
const COMPARATOR_FALLBACK = [
  { value: 'GreaterThan', labelKey: 'scheduled.common.comparatorGreaterThan' },
  { value: 'GreaterThanOrEqual', labelKey: 'scheduled.common.comparatorGreaterThanOrEqual' },
  { value: 'LessThan', labelKey: 'scheduled.common.comparatorLessThan' },
  { value: 'LessThanOrEqual', labelKey: 'scheduled.common.comparatorLessThanOrEqual' },
  { value: 'EqualTo', labelKey: 'scheduled.common.comparatorEqualTo' },
  { value: 'NotEqualTo', labelKey: 'scheduled.common.comparatorNotEqualTo' },
];

export default {
  name: 'ScheduledEdit',
  components: {
    'fusion-dropdown': FusionDropdown,
  },
  props: {
    task: {
      type: Object,
      required: false,
      default: null,
    },
  },
  emits: ['changes', 'save', 'cancel'],
  created() {
    this.form = this.initForm(this.task);
    if (this.is_watcher && this.is_edit) this.loadConditionFields();
  },
  data() {
    return {
      form: this.initForm(null),
      // OneTime is deliberately absent. CadenceCalculator returns null for it by design ("a one-time task
      // does not recur") and both create and update reject a null NextRunAt as "Trigger yields no future
      // run" — so picking it here always failed. Offering a choice that can never work is worse than not
      // offering it; making it work is cadence territory (NPM-2).
      cadences: ['Daily', 'Weekdays', 'Weekly', 'Monthly'],
      error_message: '',
      saving: false,
      // The zone picker is disclosed, not shown at rest. See the template for why.
      editing_timezone: false,
      // Loaded from the server for a watcher being edited: the fields its tool can compare and the
      // comparators its template allows. Empty until loaded, and empty is not an error.
      condition_fields: [],
      condition_comparators: [],
    };
  },
  computed: {
    ...mapStores(useMainStore),
    is_edit() {
      return !!(this.task && this.task.id);
    },
    is_watcher() {
      return this.form.taskType === 'Watcher';
    },
    // The test the server's TaskTrigger.IsEventDriven makes, case-insensitive for the same reason: a task
    // stored as "event" is still an event task, and must not get clock controls it has no use for.
    is_event_driven() {
      return String(this.form.trigger.type || '').toLowerCase() === 'event';
    },
    timezone_options() {
      // Keeps whatever the task already carries selectable even when it is not one of ours — the field
      // this replaced was free text, so stored values include Windows ids and European zones.
      //
      // Derived from the value the task ARRIVED with, not the one currently selected. Using the current
      // value made the odd one out vanish as soon as you picked something else, so a mis-click became a
      // one-way door with no way back to the zone the task actually had.
      return timezoneOptionsFor(this.form.originalTimeZone);
    },
    // The same list with its i18n keys resolved, in the {value, label} shape FusionDropdown reads.
    timezone_dropdown_options() {
      return timezoneDropdownOptions(this.timezone_options, key => this.$t(key));
    },
    weekday_options() {
      return weekdayDropdownOptions().map(o => ({ ...o, label: this.weekdayLabel(o.value) || o.label }));
    },
    // From the time the task ARRIVED with, for the reason timezone_options gives: an off-grid time must
    // stay on offer after something else is picked.
    time_dropdown_options() {
      const arrived = this.task && this.task.trigger && this.task.trigger.time;
      return timeOfDayOptions(arrived || this.form.trigger.time);
    },
    // The cadences on offer, plus the one the task ARRIVED with when it is not among them — a watcher is
    // created Hourly, which this list does not offer, so the picker matched nothing and showed its
    // placeholder for every watcher. From the arrival value for the reason timezone_options gives: picking
    // something else must not make the task's own cadence vanish from the list.
    cadence_options() {
      const options = this.cadences.map(c => ({ value: c, label: this.cadenceLabel(c) }));
      const stored = this.task && this.task.trigger && this.task.trigger.cadence;
      if (!stored || this.cadences.includes(stored)) return options;
      // Worded as ScheduledCreate words it: "continuously" is what the person chose, hourly is what it means.
      // NPM-225: an "every cycle" watcher carries its period; the cadence name alone says nothing.
      if (stored === 'Interval') {
        return [...options, { value: stored, label: this.$t('scheduled.common.cadenceInterval', { minutes: this.task.trigger.intervalMinutes ?? '?' }) }];
      }
      return [...options, { value: stored, label: stored === 'Hourly' ? 'Continuously (every hour)' : stored }];
    },
    // What the collapsed row says. Falls back to the raw id rather than to nothing: a zone we do not
    // carry in the list is exactly the case the person most needs to be able to read back.
    selected_timezone_label() {
      const match = this.timezone_options.find(z => z.value === this.form.trigger.timeZone);
      if (!match) return this.form.trigger.timeZone;
      return match.labelKey ? this.$t(match.labelKey) : match.label;
    },
    all_dealers() {
      return this.mainStore.all_dealers;
    },
    // The bare CIF when the name is not to hand, never nothing — same ladder the task rows use.
    store_label() {
      const dealers = Array.isArray(this.all_dealers) ? this.all_dealers : [];
      const dealer = dealers.find(d => d && d.cifNumber === this.active_cif);
      return dealer?.name || this.active_cif || '—';
    },
    // Always includes the watcher's current field, so a tool with no metadata leaves the form showing the
    // truth instead of an empty picker that silently offers to change it.
    condition_field_options() {
      const current = this.form.condition && this.form.condition.filterField;
      const loaded = this.condition_fields || [];
      return current && !loaded.includes(current) ? [current, ...loaded] : loaded;
    },
    // The server's list wins — it is narrowed to what the watcher's template allows, so the save cannot
    // refuse what the form offered. The fallback exists for a condition stored without a comparator at all
    // (rows created before it could be expressed) and for a failed load: an empty picker there would let
    // someone save `undefined` over a working watcher.
    condition_comparator_options() {
      const loaded = this.condition_comparators || [];
      if (loaded.length) return loaded;
      const current = this.form.condition && this.form.condition.comparator;
      // fusion-dropdown renders `label`, while the fallback carries i18n keys (FUS-76), so each option is
      // given its rendered label here — without it the fallback list showed blank rows.
      const known = COMPARATOR_FALLBACK.map(c => ({ ...c, label: this.comparatorLabel(c) }));
      return current && !known.some(c => c.value === current)
        ? [{ value: current, labelKey: '', label: current }, ...known]
        : known;
    },
    inapp_enabled() {
      return (this.form.delivery.channels || []).includes('InApp');
    },
    // The server requires a prompt only for ScheduledQuestion; a watcher's work is its compiled condition.
    // Mirrored here so the form is not stricter than the API.
    prompt_required() {
      return this.form.taskType === 'ScheduledQuestion';
    },
    // Deployment-wide, not per-rooftop: the NPM-8 beta flag governs whether scheduled tasks exist for a
    // store at all, so it cannot keep the anchor from stores already in the beta. Off until a real store
    // calendar backs IStoreCalendar (NPM-28) — with no hours on file an anchored task waits indefinitely,
    // so offering the choice sooner would only let someone build a task that never fires.
    scheduled_availability() {
      return this.mainStore.scheduled_availability;
    },
    anchor_available() {
      return !!(this.scheduled_availability && this.scheduled_availability.storeHoursAnchor);
    },
    // The deployment flag above, narrowed by what the scheduler can honour. An anchor resolves ONE fire time
    // a day, so an hourly task has no store-hours form and the API refuses the pairing — offering the control
    // would only let someone build a save that fails.
    anchor_offered() {
      // Interval (NPM-225) fires many times a day too, so the same refusal applies.
      return this.anchor_available && !['Hourly', 'Interval'].includes(this.form.trigger.cadence);
    },
    is_anchored() {
      return this.form.trigger.storeHoursAnchor !== 'None';
    },
    anchor_options() {
      return [
        { value: 'None', label: this.$t('scheduled.common.anchorNone') },
        { value: 'Open', label: this.$t('scheduled.common.anchorOpen') },
        { value: 'Close', label: this.$t('scheduled.common.anchorClose') },
      ];
    },
    // Says the offset back in words, because a signed number is the one part of this form a person can
    // read the wrong way round.
    anchor_summary() {
      return this.$t('scheduled.edit.anchorSummary', { anchor: describeAnchor(this.form.trigger.storeHoursAnchor, this.form.trigger.anchorOffsetMinutes) });
    },
    email_enabled() {
      return (this.form.delivery.channels || []).includes('Email');
    },
    push_enabled() {
      return (this.form.delivery.channels || []).includes('Push');
    },
    /**
     * Whether this deployment can send a push at all. Read from the same availability answer the panel
     * already makes before deciding what to render — the pattern storeHoursAnchor established.
     */
    push_available() {
      return !!(this.scheduled_availability && this.scheduled_availability.pushNotifications);
    },
    // The store the task runs against: keep the task's own CIF on edit, else the active store.
    active_cif() {
      return (this.task && this.task.dealerId)
        || this.mainStore.current_cif
        || (this.mainStore.active_dealer && this.mainStore.active_dealer.cifNumber)
        || '';
    },
  },
  methods: {
    // Cadence and weekday values are the server's enum names / ordinals and stay untranslated as VALUES;
    // only the option label goes through i18n. Unknown values fall back to the raw value rather than a
    // blank option.
    cadenceLabel(value) {
      const keys = {
        Daily: 'scheduled.common.cadenceDaily',
        Weekdays: 'scheduled.common.cadenceWeekdays',
        Weekly: 'scheduled.common.cadenceWeekly',
        Monthly: 'scheduled.common.cadenceMonthly',
      };
      return keys[value] ? this.$t(keys[value]) : value;
    },
    weekdayLabel(index) {
      const keys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const key = keys[index];
      return key ? this.$t(`common.days.${key}`) : '';
    },
    // A comparator option carries either a labelKey (the localized fallback list below) or a label
    // straight from the server (already human words, server-supplied data stays untranslated).
    comparatorLabel(option) {
      return option.labelKey ? this.$t(option.labelKey) : option.label;
    },
    initForm(source) {
      const t = source || {};
      const tr = t.trigger || {};
      // Channels are de-duplicated on the way in. A live task had accumulated thirteen copies of InApp
      // before the serialisation fix landed, and loading such a row into the form would save them straight
      // back. This is not the one-time sweep those rows still need — it just stops the editor adding to it.
      const channels = [...new Set((t.delivery && t.delivery.channels) || ['InApp'])];
      return {
        id: t.id || null,
        name: t.name || '',
        prompt: t.prompt || '',
        taskType: t.taskType || 'ScheduledQuestion',
        enabled: t.enabled !== undefined ? t.enabled : true,
        // Carried so the payload can send them back. The server no longer erases a condition on silence,
        // but a form that stays silent about a watcher's condition is relying entirely on that guard.
        condition: t.condition || null,
        autonomy: t.autonomy || null,
        delivery: { channels, emailEnabled: channels.includes('Email') },
        trigger: {
          // The task's own type, carried so the payload can send it back. Hardcoded to 'Time' here and in
          // buildPayload, every save of an event trigger — even a rename — put it back on the clock while it
          // kept its events, so it fired daily at 07:00 as well as on every event (PR 1006 review).
          type: tr.type || 'Time',
          cadence: tr.cadence || 'Daily',
          // Padded as MorningBriefOptIn pads it: the picker matches by ===, so "7:05" would show nothing.
          time: padTime(tr.time) || '07:00',
          // A new task defaults to the zone the USER is actually in. It used to default to a fixed
          // American one, so a task asked for at 07:00 in Paris was stamped New York and fired at 13:00 on
          // its owner's clock — the cadence math was right, its input was not.
          //
          // The default before that was the Windows id "Eastern Standard Time" — equivalent to the server,
          // but absent from the canonical list, so every new task added a stray entry to its own dropdown.
          // That still holds for whatever the browser reports: timezoneOptionsFor keeps an unlisted value
          // selectable, so a zone we do not carry shows up once, deliberately, rather than as a surprise.
          //
          // `tr.timeZone ||` still comes first, so this only ever fills a BLANK: opening an existing task
          // from a laptop in another country must not rewrite the schedule it already has. That is the same
          // read-must-not-become-a-write rule timezoneOptionsFor below exists to keep.
          timeZone: tr.timeZone || browserTimeZone() || 'America/New_York',
          dayOfWeek: tr.dayOfWeek !== undefined && tr.dayOfWeek !== null ? weekdayOrdinal(tr.dayOfWeek) : 1,
          dayOfMonth: tr.dayOfMonth || 1,
          // Read through a normaliser because this value has reached clients BOTH ways: the API
          // serialises enums by name, but the admin inventory reads the raw stored document, where the
          // same field is an ordinal. A number here would match no <option> and silently show "A set
          // time" for a task that is in fact anchored.
          storeHoursAnchor: anchorName(tr.storeHoursAnchor),
          anchorOffsetMinutes: tr.anchorOffsetMinutes || 0,
        },
        // The zone the task arrived with, kept so the picker can keep offering it even after the user
        // selects something else. Not sent in the payload — it exists only to build the option list.
        originalTimeZone: tr.timeZone || browserTimeZone() || 'America/New_York',
      };
    },
    loadConditionFields() {
      const self = this;
      // Guarded: this is a nicety on top of a form that already works from what the watcher carries, so
      // nothing about it may prevent the form from mounting. A synchronous throw here would have taken the
      // whole editor down for a task someone opened to rename.
      let request;
      try {
        request = this.mainStore.GetWatcherConditionFields(this.form.id);
      } catch (e) {
        return;
      }
      if (!request || typeof request.then !== 'function') return;
      request.then((response) => {
        self.condition_fields = (response && response.fields) || [];
        self.condition_comparators = (response && response.comparators) || [];
      }, () => {
        // Not surfaced as an error: the form still works on what the watcher already carries, and telling
        // someone their condition failed to load when they came to rename a task would be noise.
        self.condition_fields = [];
        self.condition_comparators = [];
      });
    },
    // Channels and emailEnabled are written together. TaskDelivery says email twice and delivery tests
    // both with OR, so writing one and not the other leaves the record describing itself wrongly.
    setChannel(channel, on) {
      const current = new Set(this.form.delivery.channels || []);
      if (on) current.add(channel);
      else current.delete(channel);
      // In-app is the floor: every channel off is a task that runs and tells nobody.
      if (current.size === 0) current.add('InApp');
      const channels = [...current];
      this.form.delivery = { channels, emailEnabled: channels.includes('Email') };
      this.markChanged();
    },
    markChanged() {
      this.$emit('changes', true);
    },
    validate() {
      if (!this.form.name || !this.form.name.trim()) {
        this.error_message = this.$t('scheduled.edit.errorName');
        return false;
      }
      // Not for an event trigger: its instruction is optional, blank meaning "just tell me it happened", and
      // the server accepts a blank one for exactly that reason. Requiring it here made an event task created
      // without one impossible to save from this form at all — even to rename it.
      if (this.prompt_required && !this.is_event_driven && (!this.form.prompt || !this.form.prompt.trim())) {
        this.error_message = this.$t('scheduled.edit.errorPrompt');
        return false;
      }
      // An event trigger's time control is not shown, so it cannot be the reason a save is refused.
      if (!this.is_event_driven && !this.form.trigger.time) {
        this.error_message = this.$t('scheduled.edit.errorTime');
        return false;
      }
      this.error_message = '';
      return true;
    },
    // Called by the parent DialogModal via $refs.save().
    save() {
      if (this.saving) return Promise.resolve(false);
      if (!this.validate()) return Promise.resolve(false);
      const self = this;
      this.saving = true;
      return this.mainStore.SaveScheduledTask(this.buildPayload()).then((saved) => {
        self.saving = false;
        self.$emit('save', saved);
        return saved;
      }, (error) => {
        self.saving = false;
        self.error_message = self.$t('scheduled.edit.errorSave');
        return Promise.reject(error);
      });
    },
    cancel() {
      this.$emit('cancel');
    },
    buildPayload() {
      const f = this.form;
      const trigger = {
        // The type the task arrived with — see initForm. Its events need nothing from here: this form never
        // sends them, and the server keeps the stored ones when they are absent.
        type: f.trigger.type,
        cadence: f.trigger.cadence,
        time: f.trigger.time,
        // Never a hardcoded American fallback: an empty value here is what the server reads as "the
        // caller named no zone", and it answers that by resolving the owner's own — which is the correct
        // outcome, where "Eastern Standard Time" was merely a confident wrong one.
        timeZone: f.trigger.timeZone || browserTimeZone() || '',
      };
      if (f.trigger.cadence === 'Weekly') trigger.dayOfWeek = f.trigger.dayOfWeek;
      if (f.trigger.cadence === 'Monthly') trigger.dayOfMonth = f.trigger.dayOfMonth;
      // Sent only when the anchor control was actually available to the person. The API treats an absent
      // anchor as "leave this alone" and an explicit None as "clear it", so a form that never offered the
      // choice must stay SILENT: sending None from a hidden control would erase the anchor of a task this
      // form cannot even display — the same silent downgrade the nullable trigger contract exists to stop.
      if (this.anchor_offered && !this.is_event_driven) {
        trigger.storeHoursAnchor = f.trigger.storeHoursAnchor;
        trigger.anchorOffsetMinutes = f.trigger.anchorOffsetMinutes;
      }
      const payload = {
        name: f.name.trim(),
        prompt: (f.prompt || '').trim(),
        taskType: f.taskType,
        enabled: f.enabled,
        delivery: f.delivery,
        trigger,
      };
      // Sent only when the task actually has them. The PUT treats an absent field as "leave this alone",
      // so omitting a null is correct — and sending one back would be asking the server to erase it.
      if (f.condition) payload.condition = f.condition;
      if (f.autonomy) payload.autonomy = f.autonomy;
      if (f.id) payload.id = f.id;
      // Send the store CIF so the task keys on the store (admin inventory + run scoping),
      // not the enterprise account id the server would otherwise default to.
      if (this.active_cif) payload.dealerId = this.active_cif;
      return payload;
    },
  },
};
</script>
